import React, { useState, useRef } from 'react'

import { buildComponent } from '../../../../components/factory'
import { Category } from '../../../../state/models/audio'
import { useStoreState } from '../../../../hooks/state'
import { graphql } from '../../../../services/graphql'
import { storage } from '../../../../services/firebase'

import * as UI from '@mantine/core'
import * as Icons from '../../../../components/icons'

const CategoryImageUpload = buildComponent<{
  category?: Category
  onUpdate: (updates: Partial<Category>) => void
}>()
  .withLifecycle(({ props }) => {
    const [uploading, setUploading] = useState(false)
    const resetRef = useRef<() => void>(null)

    const directoryPath =
      'images/categories/' + (props.category?.slug ?? 'temp')
    const imageName = props.category?.image ?? 'image'
    const imageExtension = '.webp'
    const imageUrl = directoryPath + '/' + imageName + imageExtension

    const onChange = async (file: File | null) => {
      if (!file || !props.category) {
        return
      }

      try {
        setUploading(true)
        const timestamp = Date.now()
        const newImageName = `${timestamp}-image`

        // Upload new image
        await storage()
          .ref(directoryPath + '/' + newImageName + imageExtension)
          .put(file)

        // Also upload as default image
        await storage()
          .ref(directoryPath + '/image' + imageExtension)
          .put(file)

        resetRef.current?.()
        props.onUpdate({ image: newImageName })
      } catch (error) {
        console.error('Erreur lors du chargement:', error)
      } finally {
        setUploading(false)
      }
    }

    return { imageUrl, onChange, resetRef, uploading }
  })
  .withRender(({ lifecycle }) => (
    <UI.Box>
      <UI.FileButton
        onChange={lifecycle.onChange}
        accept="image/webp"
        resetRef={lifecycle.resetRef}
      >
        {(buttonProps) => (
          <UI.UnstyledButton {...buttonProps} sx={{ width: '100%' }}>
            <UI.Card
              withBorder
              p={0}
              sx={{
                width: 255,
                height: 180,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: lifecycle.uploading ? 'not-allowed' : 'pointer',
                opacity: lifecycle.uploading ? 0.7 : 1,
                position: 'relative',
              }}
            >
              {lifecycle.uploading ? (
                <UI.Loader size="md" />
              ) : (
                <UI.Image
                  width={255}
                  height={180}
                  withPlaceholder
                  src={`${process.env.REACT_APP_STATIC_URL as string}${
                    lifecycle.imageUrl
                  }`}
                  placeholder={
                    <UI.Stack align="center" spacing="xs">
                      <Icons.FontAwesomeIcon
                        icon={Icons.Solid.faImage}
                        size="2x"
                        color="#ccc"
                      />
                      <UI.Text size="sm" color="dimmed">
                        Cliquez pour charger une image
                      </UI.Text>
                    </UI.Stack>
                  }
                />
              )}
            </UI.Card>
          </UI.UnstyledButton>
        )}
      </UI.FileButton>
    </UI.Box>
  ))

export const SectionImage = buildComponent<{ categoryId: string }>()
  .withLifecycle(({ props }) => {
    const category = useStoreState((store) =>
      store.audio.categories.find((c: Category) => c._id === props.categoryId),
    )

    const [loading, setLoading] = useState(false)

    const update = async (updates: Partial<Category>) => {
      if (!category) {
        return
      }

      try {
        setLoading(true)
        await graphql.mutations.updateCategory({
          _id: category._id,
          ...updates,
        })
      } finally {
        setLoading(false)
      }
    }

    return { category, loading, update }
  })
  .withRender(({ lifecycle }) => (
    <UI.Stack sx={{ flex: 1 }} spacing={24}>
      <UI.Input.Wrapper
        label={<b style={{ fontSize: '2rem' }}>Image de la catégorie</b>}
        description="Image rectangulaire utilisée pour identifier la catégorie. Format WEBP."
      >
        <UI.Group position="center" mt={16}>
          <CategoryImageUpload
            category={lifecycle.category}
            onUpdate={lifecycle.update}
          />
        </UI.Group>
      </UI.Input.Wrapper>

      {lifecycle.loading && (
        <UI.Loader
          sx={{ position: 'absolute', top: '1rem', right: '1rem' }}
          size="xs"
        />
      )}
    </UI.Stack>
  ))
